import moment from 'moment';
import { getFullName, getDaysFromNow } from '../utils/common';
import AppConstants from '../constants/AppConstants';
import { AddressRes, PayRateRes, StaffReq, StaffRes } from '../proxy/proxy';

class FormattedStaff {
  id: number | undefined;
  role: string | undefined;
  employmentType: string | undefined;
  firstName: string | undefined;
  middleName: string | undefined;
  lastName: string | undefined;
  name: string | undefined;
  dateOfBirthUtc: any;
  phoneNumber: string | undefined;
  emergencyContactFullName: string | undefined;
  emergencyContactPhone: string | undefined;
  email: string | undefined;
  emailConfirmed: boolean | undefined;
  abn: string | undefined;
  address: AddressRes | undefined;
  extDriverId: string | undefined;
  drivingLicenseNumber: string | undefined;
  drivingLicenseClasses: Array<any> = [];
  drivingLicenseIssuedByStateId: number | undefined;
  drivingLicenseRestriction: string | undefined;
  drivingLicenseFirstIssuedOnUtc: any;
  drivingLicenseExpiresOnUTC: any;
  visaType: string | undefined;
  visaExpiryDateUtc: any;
  rsatCertificateNumber: string | undefined;
  rsatExpiryDateUtc: any;
  drivingRecordEmissionDateUtc: any;
  australianFederalPoliceCheckEmissionDateUtc: any;
  payRate: PayRateRes | undefined;
  isRegisteredForGST: boolean | undefined;
  alerts: Array<any> | undefined;
  appUsagePattern: number | undefined;
  recepientCreatedTaxInvoice: boolean | undefined | null;
  rawStaffData: StaffReq;

  constructor(rawData: StaffRes) {
    this.id = rawData?.id;
    this.role = rawData?.role ?? undefined;
    this.employmentType = rawData?.employmentType?.toString();

    this.firstName = rawData?.firstName ?? undefined;
    this.middleName = rawData?.middleName ?? undefined;
    this.lastName = rawData?.lastName ?? undefined;
    this.name = getFullName(rawData?.firstName ?? '', rawData?.middleName ?? '', rawData?.lastName ?? '');

    this.phoneNumber = rawData?.phoneNumber ?? undefined;
    this.email = rawData?.email ?? undefined;
    this.emailConfirmed = rawData?.emailConfirmed;
    this.abn = rawData?.abn ?? undefined;
    this.dateOfBirthUtc = rawData?.dateOfBirthUtc;

    this.emergencyContactFullName = rawData.emergencyContactFullName ?? undefined;
    this.emergencyContactPhone = rawData.emergencyContactPhone ?? undefined;

    this.address = rawData?.address;
    this.extDriverId = rawData?.extDriverId ?? undefined;
    this.drivingLicenseExpiresOnUTC = rawData.drivingLicenseExpiresOnUTC;
    this.drivingLicenseNumber = rawData.drivingLicenseNumber ?? undefined;
    this.drivingLicenseClasses = (rawData.appUserLicenseClassMap || []).map((x: any) => x.drivingLicenseClass);
    this.drivingLicenseIssuedByStateId = rawData.drivingLicenseIssuedByStateId ?? undefined;
    this.drivingLicenseRestriction = rawData.drivingLicenseRestriction ?? undefined;
    this.drivingLicenseFirstIssuedOnUtc = rawData.drivingLicenseFirstIssuedOnUtc;
    this.drivingLicenseExpiresOnUTC = rawData.drivingLicenseExpiresOnUTC;
    this.visaType = rawData.visaType ?? undefined;
    this.visaExpiryDateUtc = rawData.visaExpiryDateUtc;
    this.rsatCertificateNumber = rawData.rsatCertificateNumber ?? undefined;
    this.rsatExpiryDateUtc = rawData.rsatExpiryDateUtc;
    this.drivingRecordEmissionDateUtc = rawData.drivingRecordEmissionDateUtc;
    this.australianFederalPoliceCheckEmissionDateUtc = rawData.australianFederalPoliceCheckEmissionDateUtc;

    this.payRate = rawData.payRate;
    this.isRegisteredForGST = rawData.isRegisteredForGST;

    this.appUsagePattern = rawData.appUsagePattern;
    this.recepientCreatedTaxInvoice = rawData.recepientCreatedTaxInvoice;

    this.rawStaffData = FormattedStaff.mapStaffResToReq(rawData);

    this.alerts = [];
    const expieryAlert = this.generateDrivingLicenceExpiryAlert();
    if (expieryAlert) {
      this.alerts.push(expieryAlert);
    }
  }

  static mapStaffResToReq(rawData: StaffRes): StaffReq {
    return {
      role: rawData.role ?? '',
      employmentType: rawData.employmentType,
      firstName: rawData.firstName ?? '',
      middleName: rawData.middleName,
      lastName: rawData.lastName ?? '',
      phoneNumber: rawData.phoneNumber ?? '',
      email: rawData.email ?? '',
      abn: rawData.abn,
      dateOfBirthUtc: rawData.dateOfBirthUtc,
      emergencyContactFullName: rawData.emergencyContactFullName,
      emergencyContactPhone: rawData.emergencyContactPhone,
      address: {
        /** Free text */
        street: rawData.address?.street,
        /** Or Pincode */
        areaCode: rawData.address?.areaCode ?? '',
        /** Or District */
        suburb: rawData.address?.suburb,
        /** Or Town */
        city: rawData.address?.city,
        /** Or Province */
        stateId: rawData.address?.stateId ?? 0,
        latitude: rawData.address?.latitude,
        longitude: rawData.address?.longitude
      },
      extDriverId: rawData.extDriverId,
      drivingLicenseNumber: rawData.drivingLicenseNumber,
      drivingLicenseIssuedByStateId: rawData.drivingLicenseIssuedByStateId,
      drivingLicenseRestriction: rawData.drivingLicenseRestriction,
      drivingLicenseFirstIssuedOnUtc: rawData.drivingLicenseFirstIssuedOnUtc,
      drivingLicenseExpiresOnUTC: rawData.drivingLicenseExpiresOnUTC,
      visaType: rawData.visaType,
      visaExpiryDateUtc: rawData.visaExpiryDateUtc,
      rsatCertificateNumber: rawData.rsatCertificateNumber,
      rsatExpiryDateUtc: rawData.rsatExpiryDateUtc,
      drivingRecordEmissionDateUtc: rawData.drivingRecordEmissionDateUtc,
      australianFederalPoliceCheckEmissionDateUtc: rawData.australianFederalPoliceCheckEmissionDateUtc,
      appUsagePattern: rawData.appUsagePattern,
      recepientCreatedTaxInvoice: rawData.recepientCreatedTaxInvoice,
    };
  }

  generateDrivingLicenceExpiryAlert() {
    if (this.drivingLicenseExpiresOnUTC) {
      const expiresInDays = getDaysFromNow(this.drivingLicenseExpiresOnUTC);
      if (expiresInDays === 0) {
        return {
          type: 'warning',
          category: 'license',
          message: 'Driving License Expires Today'
        };
      }
      if (expiresInDays < 0) {
        return {
          type: 'error',
          category: 'license',
          message: `Driving License Expired ${moment.duration(expiresInDays, 'days').humanize()} ago`
        };
      }
      if (expiresInDays < AppConstants.DRIVING_LICENSE_EXPIERY_WARNING_BEFORE_DAYS) {
        return {
          type: 'warning',
          category: 'license',
          message: `Driving License will expire in ${moment.duration(expiresInDays, 'days').humanize()}`
        };
      }
    }
    return null;
  }
}

export default FormattedStaff;
